/**
 * SerpAPI Google Shopping Lite Service
 * Routes calls through a Supabase Edge Function proxy to avoid CORS issues.
 * The edge function uses the server-side SERP_API_KEY env var.
 *
 * API Docs: https://serpapi.com/google-shopping-api
 */

const SUPABASE_URL = import.meta.env?.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env?.VITE_SUPABASE_ANON_KEY;
const SERP_PROXY_URL = SUPABASE_URL
  ? `${SUPABASE_URL}/functions/v1/serp-search`
  : null;

import { getChatCompletion } from './aiIntegrations/chatCompletion';

/**
 * Generate a short product description using Perplexity for a given product name/vendor.
 * Falls back to empty string if the call fails.
 *
 * @param {string} productName - The product title
 * @param {string} vendor - The vendor/source name
 * @returns {Promise<string>} A short 1-2 sentence product description
 */
const generateProductDescription = async (productName, vendor) => {
  if (!productName?.trim()) return '';
  try {
    const messages = [
      {
        role: 'system',
        content: 'You are a concise product copywriter. Write a short, factual 1-2 sentence product description based on the product name. Focus on key features and use cases. Do not include price, availability, or retailer info. Return only the description text, no quotes or labels.',
      },
      {
        role: 'user',
        content: `Write a short product description for: "${productName}"${vendor ? ` sold by ${vendor}` : ''}.`,
      },
    ];
    const response = await getChatCompletion(
      'PERPLEXITY',
      'perplexity/sonar',
      messages,
      { max_tokens: 80, temperature: 0.4 }
    );
    const content = response?.choices?.[0]?.message?.content || '';
    return content?.trim();
  } catch (err) {
    console.warn('[SerpAPI] Perplexity description generation failed:', err?.message);
    return '';
  }
};

/**
 * Search Google Shopping Lite for a single query via the Supabase proxy.
 *
 * @param {string} query - The search query (generated by Perplexity)
 * @param {object} options
 * @param {number} [options.maxResults=5] - Max number of results to return
 * @param {string} [options.country='us'] - Two-letter country code (gl), e.g. 'us', 'uk', 'fr'
 * @param {string} [options.language='en'] - Two-letter language code (hl), e.g. 'en', 'es', 'fr'
 * @param {string} [options.location] - Geographic location string, e.g. 'Austin, Texas, United States'
 * @param {number} [options.minPrice] - Lower bound of price range filter
 * @param {number} [options.maxPrice] - Upper bound of price range filter
 * @returns {Promise<Array>} Array of normalized product objects
 */
export const searchGoogleShopping = async (query, options = {}) => {
  if (!SERP_PROXY_URL) {
    console.warn('[SerpAPI] VITE_SUPABASE_URL is not set — skipping Google Shopping search');
    return [];
  }
  if (!query?.trim()) return [];

  const { maxResults = 5, country = 'us', language = 'en', location = 'United States', minPrice, maxPrice } = options;

  const requestBody = {
    query: query?.trim(),
    engine: 'google_shopping',
    gl: country,
    hl: language,
    num: String(maxResults + 15), // fetch extra buffer to survive rating + price filtering
    no_cache: false,
  };

  // Localization: geographic location (cannot be used together with uule)
  if (location && typeof location === 'string' && location?.trim()) {
    requestBody.location = location?.trim();
  }

  // Advanced filters: price range
  if (minPrice != null && minPrice !== '') {
    requestBody.min_price = minPrice;
  }
  if (maxPrice != null && maxPrice !== '') {
    requestBody.max_price = maxPrice;
  }

  try {
    const response = await fetch(SERP_PROXY_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
        'apikey': SUPABASE_ANON_KEY,
      },
      body: JSON.stringify(requestBody),
    });

    if (!response?.ok) {
      const errorText = await response?.text();
      console.error('[SerpAPI] Proxy request failed:', response?.status, errorText);
      return [];
    }

    const data = await response?.json();

    if (data?.error) {
      console.error('[SerpAPI] Proxy returned error:', data?.error, data?.details);
      return [];
    }

    const shoppingResults = data?.shopping_results || [];

    // Normalize results to the shape OptionsManagement expects
    const normalized = shoppingResults
      ?.filter((item) => item?.rating == null || item?.rating >= 3.0) // relaxed from 3.3 → 3.0 to keep more candidates
      ?.slice(0, maxResults)
      ?.map((item) => {
        // item.link = direct retailer URL when available (google_shopping engine with direct_link=true)
        // item.product_link = Google Shopping page URL (google.com/shopping/product/...) — NOT a retailer link
        // Only use a URL if it is NOT a Google-domain URL
        const isGoogleUrl = (url) => !url || /google\.(com|ad|co\.|com\.)/i?.test(url);
        const directRetailerUrl = (!isGoogleUrl(item?.link) ? item?.link : '') || '';
        // Fall back to Google Shopping link when direct retailer link is unavailable or blocked
        const productUrl = directRetailerUrl || item?.product_link || '';

        return {
          name: item?.title || '',
          vendor: item?.source || item?.merchant?.name || '',
          price: item?.extracted_price != null
            ? String(item?.extracted_price?.toFixed(2))
            : item?.price?.replace(/[^0-9.]/g, '') || '',
          productUrl: productUrl,
          imageUrl: item?.thumbnail || '',
          description: item?.snippet || '',
          keySpecs: item?.extensions?.join(' · ') || '',
          reviewScore: item?.rating != null ? String(item?.rating) : null,
          reviewMaxScore: item?.rating != null ? '5' : null,
          reviewCount: item?.reviews != null ? String(item?.reviews) : null,
          // Mark as Google Shopping sourced — price is live/verified
          priceVerified: item?.extracted_price != null,
          priceUnconfirmed: item?.extracted_price == null,
          // Raw SerpAPI data for debugging
          _serpRaw: item,
        };
      });

    // Enrich descriptions via Perplexity for items missing a snippet
    const enriched = await Promise.all(
      normalized?.map(async (product) => {
        if (!product?.description) {
          const generated = await generateProductDescription(product?.name, product?.vendor);
          return { ...product, description: generated };
        }
        return product;
      })
    );

    return enriched;
  } catch (err) {
    console.error('[SerpAPI] Fetch error:', err?.message);
    return [];
  }
};

/**
 * Execute multiple search queries in parallel and merge/deduplicate results.
 *
 * @param {string[]} queries - Array of search queries from Perplexity
 * @param {object} options - Same options as searchGoogleShopping
 * @returns {Promise<Array>} Deduplicated, merged array of product results
 */
export const searchMultipleQueries = async (queries, options = {}) => {
  if (!queries?.length) return [];

  const targetFinal = options?.maxResults || 5;
  // Fetch a much larger pool per query so post-filtering has enough candidates
  const resultsPerQuery = Math.ceil((targetFinal * 3) / queries?.length) + 3;

  const allResults = await Promise.all(
    queries?.map((q) => searchGoogleShopping(q, { ...options, maxResults: resultsPerQuery }))
  );

  // Flatten and deduplicate by product URL
  const seen = new Set();
  const merged = [];
  for (const batch of allResults) {
    for (const item of batch) {
      const key = item?.productUrl || item?.name?.toLowerCase();
      if (key && !seen?.has(key)) {
        seen?.add(key);
        merged?.push(item);
      }
    }
  }

  // Return the full merged pool (up to 3× target) — let the caller trim after post-filtering
  return merged?.slice(0, targetFinal * 3);
};
